import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { getDailyLocation, veseelViewHistory } from 'services/api-service/center';
import { MomentService } from 'services/moment-service';
import { IStoreState } from 'store/modules';

interface MapOverlayProps {
  userinfo: any;
  lng: string;
  lat: string;
  mdn: string;
  isExpire: boolean;
  onChange: (lng: number, lat: number, date: string, mdn: string) => void;
}

const MapCoordinate = ({ userinfo, lng, lat, mdn, isExpire, onChange }: MapOverlayProps) => {
  const toastMessage = (message: string) => <div style={{ fontSize: '1.1rem' }}>{message}</div>;
  const vessels = useSelector((store: IStoreState) => store.MainReducer.vessels);
  const [coordinateType, setCoordinateType] = useState('2');
  const [selectMdn, setSelectMdn] = useState('');
  const [position, setPosition] = useState({
    lng: 0,
    lngDeg: 0,
    lngMin: 0,
    lngSec: '0',
    lngDecMin: '0',
    lngDir: 'E',
    lat: 0,
    latDeg: 0,
    latMin: 0,
    latSec: '0',
    latDecMin: '0',
    latDir: 'N',
  });

  const isDecimalInput = (value: string) => /^\d*\.?\d*$/.test(value);
  const isIntegerInput = (value: string) => /^\d*$/.test(value);
  const selectAllOnFocus = (event: React.FocusEvent<HTMLInputElement>) => event.target.select();

  const onChangeSelect = (e) => {
    setCoordinateType(e.target.value);
    convertToDms(position.lng, true);
    convertToDms(position.lat, false);
  };

  const convertToDms = (dd, isLng) => {
    const dir = dd < 0 ? (isLng ? 'W' : 'S') : isLng ? 'E' : 'N';
    const absDd = Math.abs(Number(dd)) || 0;
    let total = Math.round(absDd * 3600 * 100);
    const sec = (total % 6000) / 100;
    total = Math.floor(total / 6000);
    const min = total % 60;
    const deg = Math.floor(total / 60);
    const decMin = Math.round((min + sec / 60) * 10000) / 10000;
    if (isLng) {
      setPosition((prevState) => ({
        ...prevState,
        lng: dd,
        lngDeg: deg,
        lngMin: min,
        lngSec: String(sec),
        lngDecMin: String(decMin),
        lngDir: dir,
      }));
    } else {
      setPosition((prevState) => ({
        ...prevState,
        lat: dd,
        latDeg: deg,
        latMin: min,
        latSec: String(sec),
        latDecMin: String(decMin),
        latDir: dir,
      }));
    }
  };

  const ConvertDMSToDD = (degrees, minutes, seconds, direction) => {
    let dd = Number(degrees) + Number(minutes) / 60 + Number(seconds) / (60 * 60);

    if (direction == 'S' || direction == 'W') {
      dd = dd * -1;
    } // Don't do anything for N or E
    return dd;
  };

  const handleChangeDD = (event: React.ChangeEvent<HTMLInputElement>, isLng: boolean) => {
    convertToDms(event.target.value, isLng);
  };

  const handleChangeDeg = (event: React.ChangeEvent<HTMLInputElement>, isLng: boolean) => {
    const value = event.target.value;
    if (!isIntegerInput(value)) return;
    const isDm = coordinateType === '4';
    if (isLng) {
      const min = isDm ? position.lngDecMin : position.lngMin;
      const sec = isDm ? 0 : position.lngSec;
      const lng = ConvertDMSToDD(value, min, sec, position.lngDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lng: Number(lng),
        lngDeg: Number(value),
      }));
    } else {
      const min = isDm ? position.latDecMin : position.latMin;
      const sec = isDm ? 0 : position.latSec;
      const lat = ConvertDMSToDD(value, min, sec, position.latDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lat: Number(lat),
        latDeg: Number(value),
      }));
    }
  };
  const handleChangeMin = (event: React.ChangeEvent<HTMLInputElement>, isLng: boolean) => {
    const value = event.target.value;
    if (!isIntegerInput(value)) return;
    if (isLng) {
      const lng = ConvertDMSToDD(position.lngDeg, value, position.lngSec, position.lngDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lng: Number(lng),
        lngMin: Number(value),
      }));
    } else {
      const lat = ConvertDMSToDD(position.latDeg, value, position.latSec, position.latDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lat: Number(lat),
        latMin: Number(value),
      }));
    }
  };
  const handleChangeSec = (event: React.ChangeEvent<HTMLInputElement>, isLng: boolean) => {
    const value = event.target.value;
    if (!isDecimalInput(value)) return;
    if (isLng) {
      const lng = ConvertDMSToDD(position.lngDeg, position.lngMin, value, position.lngDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lng: Number(lng),
        lngSec: value,
      }));
    } else {
      const lat = ConvertDMSToDD(position.latDeg, position.latMin, value, position.latDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lat: Number(lat),
        latSec: value,
      }));
    }
  };
  const handleChangeDecMin = (event: React.ChangeEvent<HTMLInputElement>, isLng: boolean) => {
    const value = event.target.value;
    if (!isDecimalInput(value)) return;
    if (isLng) {
      const lng = ConvertDMSToDD(position.lngDeg, value, 0, position.lngDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lng: Number(lng),
        lngDecMin: value,
      }));
    } else {
      const lat = ConvertDMSToDD(position.latDeg, value, 0, position.latDir).toFixed(6);
      setPosition((prevState) => ({
        ...prevState,
        lat: Number(lat),
        latDecMin: value,
      }));
    }
  };

  const onChangeVessel = async (e) => {
    const mdn = e.target.value;
    try {
      const response: any = await getDailyLocation(
        mdn,
        MomentService.getInstance().getDateFormat(new Date(), 'YYYYMMDD')
      );
      if (Array.isArray(response.data.log) && response.data.log.length > 1) {
        const lastLog = response.data.log[response.data.log.length - 1];
        onChange(lastLog.lng, lastLog.lat, lastLog.dataTime, mdn);
        setSelectMdn(mdn);
      } else {
        onChange(0, 0, '', '');
        setSelectMdn('');
      }
      const targetVessel = vessels.find((v) => v.mdn === mdn);
      await veseelViewHistory(targetVessel ? targetVessel.bbn : '', mdn);
    } catch (error) {
      console.log('getDailyLocation Error ', error);
    }
  };

  const validatePosition = (latNum: number, lngNum: number): string | null => {
    if (coordinateType === '2') {
      const parts = [position.latMin, position.lngMin, Number(position.latSec), Number(position.lngSec)];
      if (parts.some((v) => v >= 60)) return '분과 초는 60 미만으로 입력하세요.';
    }
    if (coordinateType === '4') {
      if (Number(position.latDecMin) >= 60 || Number(position.lngDecMin) >= 60) {
        return '분은 60 미만으로 입력하세요.';
      }
    }
    if (!latNum || !lngNum) return '좌표를 입력하세요.';
    if (Math.abs(latNum) > 90) return '위도 범위(0~90)를 확인하세요.';
    if (Math.abs(lngNum) > 180) return '경도 범위(0~180)를 확인하세요.';
    return null;
  };

  const onClickSetPosition = () => {
    const latNum = Number(position.lat);
    const lngNum = Number(position.lng);
    const error = validatePosition(latNum, lngNum);
    if (error) {
      toast(toastMessage(error), { type: 'error' });
      return;
    }
    onChange(lngNum, latNum, '', '');
    setSelectMdn('');
  };

  const onKeyDownPosition = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && coordinateType !== '3' && (event.target as HTMLElement).tagName === 'INPUT') {
      event.preventDefault();
      onClickSetPosition();
    }
  };

  useEffect(() => {
    if (lng !== undefined && lng !== null && lng !== '') {
      convertToDms(Number(lng).toFixed(6), true);
    }
    if (lat !== undefined && lat !== null && lat !== '') {
      convertToDms(Number(lat).toFixed(6), false);
    }
  }, [lng, lat]);

  useEffect(() => {
    if (vessels.length > 0 && mdn && vessels.some((v) => v.mdn === mdn)) {
      setSelectMdn(mdn);
    } else {
      setSelectMdn('');
    }
  }, [mdn, vessels]);

  return (
    <MapCoordinateTemplate onKeyDown={onKeyDownPosition}>
      <SelectWrap className="w120" onChange={onChangeSelect} value={coordinateType}>
        <option value="1">좌표(도)</option>
        <option value="4">좌표(도분)</option>
        <option value="2">좌표(도분초)</option>
        {(userinfo?.belong === '마량파출소' || userinfo?.belong === '서귀포파출소') && (
          <option value="3">낚시어선</option>
        )}
      </SelectWrap>
      {coordinateType === '3' ? (
        <SelectWrap value={selectMdn} disabled={isExpire} onChange={onChangeVessel}>
          <option value=""></option>
          {vessels.map((item: any, index: number) => (
            <OptionWrap key={index} value={item.mdn} state={item.state}>
              {item.bbn}
            </OptionWrap>
          ))}
        </SelectWrap>
      ) : (
        <>
          {coordinateType === '1' ? (
            <InputNumber
              value={position.lat}
              onFocus={selectAllOnFocus}
              onChange={(event) => handleChangeDD(event, false)}
            ></InputNumber>
          ) : coordinateType === '4' ? (
            <InputWrap>
              <InputNumberSmall
                value={position.latDeg}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeDeg(event, false)}
              ></InputNumberSmall>
              <Unit>&#176;</Unit>
              <InputNumberSmall
                width="70px"
                value={position.latDecMin}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeDecMin(event, false)}
              ></InputNumberSmall>
              <Unit>&#39;</Unit>
              <InputNumberSmall width="25px" disabled value={position.latDir}></InputNumberSmall>
            </InputWrap>
          ) : (
            <InputWrap>
              <InputNumberSmall
                value={position.latDeg}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeDeg(event, false)}
              ></InputNumberSmall>
              <Unit>&#176;</Unit>
              <InputNumberSmall
                width="30px"
                value={position.latMin}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeMin(event, false)}
              ></InputNumberSmall>
              <Unit>&#39;</Unit>
              <InputNumberSmall
                width="60px"
                value={position.latSec}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeSec(event, false)}
              ></InputNumberSmall>
              <Unit>&#34;</Unit>
              <InputNumberSmall width="25px" disabled value={position.latDir}></InputNumberSmall>
            </InputWrap>
          )}
          <Seperator>|</Seperator>
          {coordinateType === '1' ? (
            <InputNumber
              value={position.lng}
              onFocus={selectAllOnFocus}
              onChange={(event) => handleChangeDD(event, true)}
            ></InputNumber>
          ) : coordinateType === '4' ? (
            <InputWrap>
              <InputNumberSmall
                value={position.lngDeg}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeDeg(event, true)}
              ></InputNumberSmall>
              <Unit>&#176;</Unit>
              <InputNumberSmall
                width="70px"
                value={position.lngDecMin}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeDecMin(event, true)}
              ></InputNumberSmall>
              <Unit>&#39;</Unit>
              <InputNumberSmall width="25px" disabled value={position.lngDir}></InputNumberSmall>
            </InputWrap>
          ) : (
            <InputWrap>
              <InputNumberSmall
                value={position.lngDeg}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeDeg(event, true)}
              ></InputNumberSmall>
              <Unit>&#176;</Unit>
              <InputNumberSmall
                width="30px"
                value={position.lngMin}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeMin(event, true)}
              ></InputNumberSmall>
              <Unit>&#39;</Unit>
              <InputNumberSmall
                width="60px"
                value={position.lngSec}
                onFocus={selectAllOnFocus}
                onChange={(event) => handleChangeSec(event, true)}
              ></InputNumberSmall>
              <Unit>&#34;</Unit>
              <InputNumberSmall width="25px" disabled value={position.lngDir}></InputNumberSmall>
            </InputWrap>
          )}
        </>
      )}
      {coordinateType !== '3' && <InputButton onClick={onClickSetPosition}>위치지정</InputButton>}
    </MapCoordinateTemplate>
  );
};

const MapCoordinateTemplate = styled.div`
  display: flex;
  margin: auto;
`;
const SelectWrap = styled.select`
  width: 105px;
  height: 30px;
  margin-right: 5px;
  background: #006ac8;
  border-radius: 5px;
  border: none;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  cursor: pointer;
  :focus {
    outline: none;
  }
`;
const OptionWrap = styled.option<{ state: string }>`
  background-color: ${({ state }) => (state === 'A' ? '#003a6d' : '#006ac8')};
  color: #ffffff;
`;
const Seperator = styled.div`
  width: 4px;
  height: 19px;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  margin: 3px;
`;
const InputNumber = styled.input`
  width: 140px;
  height: 30px;
  background-color: #006ac8;
  border-radius: 5px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  margin: 5px;
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
  margin: auto;
  border: none;
  outline: none;
`;
const InputWrap = styled.div`
  display: flex;
  width: 175px;
  height: 30px;
  align-items: center;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  margin: auto;
`;
const InputNumberSmall = styled.input`
  width: ${(props) => (props.width ? props.width : '40px')};
  height: 30px;
  background-color: #006ac8;
  border-radius: 5px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  margin: 5px;
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
  margin: auto;
  border: none;
  outline: none;
`;
const Unit = styled.div`
  width: 10px;
  height: 30px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
  margin: auto;
  border: none;
  outline: none;
`;
const InputButton = styled.button`
  width: 70px;
  height: 30px;
  margin-left: 5px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  color: #ffffff;
  border-radius: 0.3125rem;
  background: #0086ff;
  border: none;
  cursor: pointer;
`;

export default MapCoordinate;
